import { useState, useCallback, useRef } from 'react';
import { useMutation } from '@apollo/client';
import { X, ImagePlus, FileText, Trash2, AlertCircle, CheckCircle, Camera, UserCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  INTERVIEWER_ATTACHMENT_PRESIGN,
  SAVE_INTERVIEWER_ATTACHMENT,
  GET_SIGNED_UPLOAD_URL,
  SAVE_PERSONAL_DATA,
  ANALYZE_PROFILE_PHOTO,
} from '@/api/graphql/mutations';
import { CURRENT_USER } from '@/api/graphql/queries';

export interface CategoryAttachment {
  _id: string;
  fileName: string;
  s3Key: string;
  contentType: string;
  fileSize: number;
  uploadedAt?: string;
  requestKey?: string;
}

interface AttachmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  categoryId: string;
  existingAttachments: CategoryAttachment[];
  onAttachmentsChange: (attachments: CategoryAttachment[]) => void;
  onUseAsAnswer?: (text: string, attachmentUrl: string) => void;
  showProfilePhotoUpload?: boolean;
  onProfilePhotoUploaded?: () => void;
  /** Tags uploads to a specific interviewer media-checklist item. */
  requestKey?: string;
  /** Standalone "just upload files" usage (news-tip quick upload): no
   *  interview is running, so the "Upload as Answer" action is hidden and
   *  the plain Upload button becomes the primary CTA. */
  quickUploadMode?: boolean;
}

// Allowed file types
const ALLOWED_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'audio/mpeg',
  'audio/wav',
  'audio/x-m4a',
  'audio/mp4',
  'audio/ogg',
  'video/mp4',
  'video/webm',
  'video/quicktime',
  'video/mov',
];

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
const MAX_FILES = 10;

interface FileWithProgress {
  file: File;
  progress: number;
  status: 'pending' | 'uploading' | 'success' | 'error';
  error?: string;
  attachment?: CategoryAttachment;
  uploadUrl?: string;
}

const getAnswerText = (file: File): string => {
  if (file.type.startsWith('image/')) return "I've added a photo as my answer.";
  if (file.type.startsWith('video/')) return "I've added a video as my answer.";
  if (file.type.startsWith('audio/')) return "I've added an audio recording as my answer.";
  if (file.type.includes('pdf') || file.type.includes('word') || file.type.includes('excel') || file.type.includes('spreadsheet'))
    return "I've added a document as my answer.";
  if (file.type === 'text/plain') return "I've added a text file as my answer.";
  return "I've added an attachment as my answer.";
};

export default function AttachmentModal({
  isOpen,
  onClose,
  categoryId,
  existingAttachments,
  onAttachmentsChange,
  onUseAsAnswer,
  showProfilePhotoUpload,
  onProfilePhotoUploaded,
  requestKey,
  quickUploadMode,
}: AttachmentModalProps) {
  const [files, setFiles] = useState<FileWithProgress[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [profilePhotoUploading, setProfilePhotoUploading] = useState(false);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState<string | null>(null);
  const [profilePhotoFeedback, setProfilePhotoFeedback] = useState<string | null>(null);
  const [profilePhotoApproved, setProfilePhotoApproved] = useState<boolean | null>(null);

  const [presignMutation] = useMutation(INTERVIEWER_ATTACHMENT_PRESIGN);
  const [saveMutation] = useMutation(SAVE_INTERVIEWER_ATTACHMENT);
  const [getSignedUploadUrl] = useMutation(GET_SIGNED_UPLOAD_URL);
  const [savePersonalData] = useMutation(SAVE_PERSONAL_DATA);
  const [analyzePhoto] = useMutation(ANALYZE_PROFILE_PHOTO);

  const handleProfilePhotoSelect = (file: File) => {
    if (!file.type.startsWith('image/')) return;
    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be under 5MB.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setProfilePhotoPreview(event.target?.result as string);
    };
    reader.readAsDataURL(file);

    // Reset feedback state for new upload
    setProfilePhotoFeedback(null);
    setProfilePhotoApproved(null);
    uploadProfilePhoto(file);
  };

  const uploadProfilePhoto = async (file: File) => {
    setProfilePhotoUploading(true);
    try {
      const ext = file.name.split('.').pop() || 'jpg';
      const fileName = `avatars/${Date.now()}.${ext}`;

      const { data } = await getSignedUploadUrl({
        variables: { fileName, contentType: file.type },
      });

      if (!data?.getSignedUploadUrl) throw new Error('Failed to get upload URL');

      const uploadUrl = data.getSignedUploadUrl;
      await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });

      const permanentUrl = uploadUrl.split('?')[0];

      // Analyze photo with Gemini vision
      const analysisResult = await analyzePhoto({
        variables: { imageUrl: permanentUrl },
      });

      const analysis = analysisResult.data?.analyzeProfilePhoto;

      if (analysis && !analysis.approved) {
        // Photo not suitable — show feedback, don't save as avatar
        setProfilePhotoApproved(false);
        setProfilePhotoFeedback(analysis.feedback || 'Please try a different photo.');
        setProfilePhotoUploading(false);
        return;
      }

      // Photo approved — save as avatar
      setProfilePhotoApproved(true);
      setProfilePhotoFeedback(analysis?.feedback || null);

      await savePersonalData({
        variables: { fields: { avatar: permanentUrl } },
        refetchQueries: [CURRENT_USER],
        awaitRefetchQueries: true,
      });

      onProfilePhotoUploaded?.();
    } catch (err) {
      console.error('Failed to upload profile photo:', err);
      setProfilePhotoPreview(null);
      setProfilePhotoFeedback(null);
      setProfilePhotoApproved(null);
      alert('Failed to upload profile photo. Please try again.');
    } finally {
      setProfilePhotoUploading(false);
    }
  };

  const validateFile = (file: File): string | null => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      return `File type not allowed: ${file.type}`;
    }
    if (file.size > MAX_FILE_SIZE) {
      return `File too large: ${(file.size / (1024 * 1024)).toFixed(1)}MB (max 50MB)`;
    }
    return null;
  };

  const addFiles = (newFiles: FileList | File[]) => {
    const fileArray = Array.from(newFiles);
    const currentCount = files.length + existingAttachments.length;

    if (currentCount + fileArray.length > MAX_FILES) {
      alert(`Maximum ${MAX_FILES} files allowed. You can add ${MAX_FILES - currentCount} more.`);
      return;
    }

    const validFiles: FileWithProgress[] = [];
    for (const file of fileArray) {
      const error = validateFile(file);
      if (error) {
        validFiles.push({ file, progress: 0, status: 'error', error });
      } else {
        validFiles.push({ file, progress: 0, status: 'pending' });
      }
    }

    setFiles(prev => [...prev, ...validFiles]);
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files) {
      addFiles(e.dataTransfer.files);
    }
  }, [addFiles]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const uploadFile = async (fileWithProgress: FileWithProgress, index: number) => {
    const { file } = fileWithProgress;

    try {
      // Get presigned URL
      const presignResult = await presignMutation({
        variables: {
          input: {
            categoryId,
            fileName: file.name,
            contentType: file.type,
            fileSize: file.size,
          },
        },
      });

      if (!presignResult.data?.interviewerAttachmentPresign?.success) {
        throw new Error(
          presignResult.data?.interviewerAttachmentPresign?.error || 'Failed to get upload URL'
        );
      }

      const { uploadUrl, s3Key } = presignResult.data.interviewerAttachmentPresign;
      console.log('[AttachmentModal] Presign success', { s3Key, uploadUrl: uploadUrl?.substring(0, 80) });

      // Update progress
      setFiles(prev =>
        prev.map((f, i) => (i === index ? { ...f, status: 'uploading', progress: 30 } : f))
      );

      // Upload to S3
      console.log('[AttachmentModal] Starting S3 PUT', { fileName: file.name, contentType: file.type, size: file.size });
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      console.log('[AttachmentModal] S3 PUT response', { status: uploadResponse.status, ok: uploadResponse.ok, statusText: uploadResponse.statusText });

      if (!uploadResponse.ok) {
        const responseText = await uploadResponse.text();
        console.error('[AttachmentModal] S3 PUT failed', { status: uploadResponse.status, body: responseText });
        throw new Error(`Upload failed: ${uploadResponse.statusText}`);
      }

      setFiles(prev =>
        prev.map((f, i) => (i === index ? { ...f, progress: 70 } : f))
      );

      // Save attachment metadata
      console.log('[AttachmentModal] Calling saveInterviewerAttachment', { categoryId, s3Key });
      const saveResult = await saveMutation({
        variables: {
          input: {
            categoryId,
            fileName: file.name,
            s3Key,
            contentType: file.type,
            fileSize: file.size,
            ...(requestKey ? { requestKey } : {}),
          },
        },
      });

      if (!saveResult.data?.saveInterviewerAttachment?.success) {
        console.error('[AttachmentModal] Save mutation failed', saveResult.data?.saveInterviewerAttachment);
        throw new Error(
          saveResult.data?.saveInterviewerAttachment?.error || 'Failed to save attachment'
        );
      }

      console.log('[AttachmentModal] Save success', saveResult.data.saveInterviewerAttachment.attachment);

      const attachment = saveResult.data.saveInterviewerAttachment.attachment;
      const publicUrl = uploadUrl.split('?')[0];

      setFiles(prev =>
        prev.map((f, i) =>
          i === index ? { ...f, status: 'success', progress: 100, attachment, uploadUrl: publicUrl } : f
        )
      );

      return { attachment, publicUrl };
    } catch (error) {
      console.error('[AttachmentModal] Upload error', error);
      setFiles(prev =>
        prev.map((f, i) =>
          i === index
            ? { ...f, status: 'error', error: error instanceof Error ? error.message : 'Upload failed' }
            : f
        )
      );
      return null;
    }
  };

  const uploadAllFiles = async (mode: 'attachment' | 'answer') => {
    // Prevent starting a new upload batch while uploads are already in progress
    if (files.some(f => f.status === 'uploading')) {
      return;
    }

    const pendingFilesWithIndex = files
      .map((file, index) => ({ file, index }))
      .filter(({ file }) => file.status === 'pending');

    if (pendingFilesWithIndex.length === 0) {
      return;
    }

    const uploadPromises = pendingFilesWithIndex.map(({ file, index }) =>
      uploadFile(file, index)
    );

    const results = await Promise.all(uploadPromises);
    const succeeded = results.filter(
      (r): r is { attachment: CategoryAttachment; publicUrl: string } => r !== null
    );

    if (succeeded.length > 0) {
      onAttachmentsChange([...existingAttachments, ...succeeded.map(r => r.attachment)]);
    }

    if (mode === 'answer' && succeeded.length > 0 && onUseAsAnswer) {
      const text =
        succeeded.length === 1
          ? getAnswerText(pendingFilesWithIndex[0].file.file)
          : "I've added multiple files as my answer.";
      onUseAsAnswer(text, succeeded[0].publicUrl);
    }

    // Close modal after upload completes
    setFiles([]);
    onClose();
  };

  const handleClose = () => {
    // Prevent closing the modal while uploads are in progress
    if (files.some(f => f.status === 'uploading')) {
      return;
    }
    const successfulUploads = files.filter(f => f.status === 'success');
    if (successfulUploads.length > 0) {
      onAttachmentsChange([...existingAttachments, ...successfulUploads.map(f => f.attachment!)]);
    }
    setFiles([]);
    onClose();
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const pendingCount = files.filter(f => f.status === 'pending').length;
  const uploadingCount = files.filter(f => f.status === 'uploading').length;
  const isUploading = uploadingCount > 0;
  const totalCount = existingAttachments.length + files.length;
  const remainingSlots = MAX_FILES - totalCount;

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-[1000] p-4"
      onClick={() => {
        if (!isUploading) {
          handleClose();
        }
      }}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-lg max-h-[80vh] flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {quickUploadMode ? 'Upload your files' : 'Add Pics, Vids & Docs'}
          </h2>
          <button
            onClick={handleClose}
            disabled={isUploading}
            className={cn(
              "p-2 rounded-full transition-colors",
              isUploading
                ? "cursor-not-allowed opacity-50"
                : "hover:bg-gray-100"
            )}
            aria-label="Close"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Profile Photo Upload */}
          {showProfilePhotoUpload && (
            <div className="mb-4">
              <button
                type="button"
                disabled={profilePhotoUploading}
                className={cn(
                  'w-full flex items-center gap-3 p-4 rounded-xl border-2 border-dashed transition-colors',
                  profilePhotoApproved === false
                    ? 'border-red-300 bg-red-50 hover:bg-red-100'
                    : profilePhotoPreview && profilePhotoApproved
                      ? 'border-green-400 bg-green-50'
                      : 'border-yellow-400 bg-yellow-50 hover:bg-yellow-100 active:bg-yellow-100',
                  profilePhotoUploading && 'opacity-60 cursor-not-allowed'
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  if (profilePhotoUploading) return;
                  const input = document.createElement('input');
                  input.type = 'file';
                  input.accept = 'image/*';
                  input.onchange = ev => {
                    const target = ev.target as HTMLInputElement;
                    if (target.files?.[0]) handleProfilePhotoSelect(target.files[0]);
                  };
                  input.click();
                }}
              >
                {profilePhotoPreview ? (
                  <img src={profilePhotoPreview} alt="Profile" className={cn(
                    'w-12 h-12 rounded-full object-cover flex-shrink-0',
                    profilePhotoApproved === false && 'ring-2 ring-red-400'
                  )} />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-yellow-200 flex items-center justify-center flex-shrink-0">
                    <UserCircle className="w-7 h-7 text-yellow-600" />
                  </div>
                )}
                <div className="text-left flex-1">
                  <p className="text-sm font-semibold text-gray-900">
                    {profilePhotoUploading
                      ? 'Analyzing photo...'
                      : profilePhotoApproved === false
                        ? 'Try a different photo'
                        : profilePhotoPreview
                          ? 'Photo uploaded!'
                          : 'Upload your proxi photo'}
                  </p>
                  <p className={cn(
                    'text-xs mt-0.5',
                    profilePhotoApproved === false ? 'text-red-600' : 'text-gray-500'
                  )}>
                    {profilePhotoFeedback
                      || (profilePhotoPreview ? 'Your photo will appear on your contributions' : 'This is how you\'ll appear on our shows')}
                  </p>
                </div>
                {profilePhotoUploading && (
                  <div className="w-5 h-5 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin flex-shrink-0" />
                )}
                {profilePhotoPreview && !profilePhotoUploading && profilePhotoApproved && (
                  <CheckCircle size={20} className="text-green-500 flex-shrink-0" />
                )}
                {profilePhotoApproved === false && !profilePhotoUploading && (
                  <AlertCircle size={20} className="text-red-400 flex-shrink-0" />
                )}
              </button>
            </div>
          )}

          {/* Separator when profile photo section is shown */}
          {showProfilePhotoUpload && (
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-1 h-px bg-gray-200" />
              <span className="text-xs text-gray-400 font-medium uppercase tracking-wider">Attach to interview</span>
              <div className="flex-1 h-px bg-gray-200" />
            </div>
          )}

          {/* Dropzone */}
          <div
            className={cn(
              'border-2 border-dashed rounded-xl p-6 text-center transition-colors cursor-pointer',
              isDragging ? 'border-primary-500 bg-primary-500/5' : 'border-gray-300 hover:border-gray-400',
              remainingSlots <= 0 && 'opacity-50 cursor-not-allowed'
            )}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => {
              if (remainingSlots > 0) {
                const input = document.createElement('input');
                input.type = 'file';
                input.multiple = true;
                input.accept = ALLOWED_TYPES.join(',');
                input.onchange = e => {
                  const target = e.target as HTMLInputElement;
                  if (target.files) addFiles(target.files);
                };
                input.click();
              }
            }}
          >
            <ImagePlus size={32} className="mx-auto mb-3 text-primary-500/60" />
            <p className="text-gray-600 font-medium">
              {showProfilePhotoUpload ? 'Add a photo, video, or file to your answer' : 'Tap to choose a file or drop it here'}
            </p>
            <p className="text-sm text-gray-400 mt-1">
              Photos, videos, audio, or documents • up to 50 MB
            </p>
          </div>

          {/* Camera Button - Mobile Only */}
          <button
            type="button"
            className={cn(
              'mt-3 w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl border-2 border-dashed border-primary-500/30 bg-primary-500/5 text-primary-600 font-medium transition-colors sm:hidden',
              remainingSlots <= 0 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-primary-500/10 active:bg-primary-500/15'
            )}
            disabled={remainingSlots <= 0}
            onClick={(e) => {
              e.stopPropagation();
              if (remainingSlots > 0) {
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = 'image/*';
                input.setAttribute('capture', 'environment');
                input.onchange = ev => {
                  const target = ev.target as HTMLInputElement;
                  if (target.files) addFiles(target.files);
                };
                input.click();
              }
            }}
          >
            <Camera size={20} />
            <span>Take Photo</span>
          </button>

          {/* Existing Attachments */}
          {existingAttachments.length > 0 && (
            <div className="mt-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2">
                Previously Uploaded ({existingAttachments.length})
              </h3>
              <div className="space-y-2">
                {existingAttachments.map(attachment => (
                  <div
                    key={attachment._id}
                    className="flex items-center gap-3 p-3 bg-primary-500/5 rounded-lg border border-primary-500/20"
                  >
                    <FileText size={20} className="text-primary-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {attachment.fileName}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatFileSize(attachment.fileSize)}
                      </p>
                    </div>
                    <CheckCircle size={20} className="text-primary-500 flex-shrink-0" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* File List */}
          {files.length > 0 && (
            <div className="mt-4 space-y-2">
              {files.map((fileItem, index) => (
                <div
                  key={`${fileItem.file.name}-${index}`}
                  className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
                >
                  <FileText size={20} className="text-gray-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {fileItem.file.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatFileSize(fileItem.file.size)}
                    </p>
                    {fileItem.status === 'uploading' && (
                      <div className="mt-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary-500 transition-all duration-300"
                          style={{ width: `${fileItem.progress}%` }}
                        />
                      </div>
                    )}
                    {fileItem.status === 'error' && (
                      <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                        <AlertCircle size={12} />
                        {fileItem.error}
                      </p>
                    )}
                  </div>
                  <div className="flex-shrink-0">
                    {fileItem.status === 'success' ? (
                      <CheckCircle size={20} className="text-primary-500" />
                    ) : fileItem.status === 'uploading' ? (
                      <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <button
                        onClick={() => removeFile(index)}
                        className="p-1 hover:bg-gray-200 rounded transition-colors"
                        aria-label="Remove file"
                      >
                        <Trash2 size={16} className="text-gray-500" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={handleClose}
            disabled={isUploading}
            className={cn(
              "px-4 py-2 rounded-lg transition-colors",
              isUploading
                ? "text-gray-400 cursor-not-allowed opacity-50"
                : "text-gray-600 hover:bg-gray-100"
            )}
          >
            Cancel
          </button>
          <button
            onClick={() => uploadAllFiles('attachment')}
            disabled={pendingCount === 0 || isUploading}
            className={cn(
              'px-4 py-2 rounded-lg transition-colors',
              quickUploadMode ? 'font-semibold shadow-sm' : 'font-medium border-2',
              pendingCount > 0 && !isUploading
                ? quickUploadMode
                  ? 'text-white hover:brightness-110'
                  : 'border-[#ec4899] text-[#ec4899] hover:bg-[#ec4899]/5'
                : quickUploadMode
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed shadow-none'
                  : 'border-gray-200 text-gray-300 cursor-not-allowed'
            )}
            // In quick-upload mode this is the only action, so it takes the
            // primary gradient treatment that "Upload as Answer" has below.
            style={
              quickUploadMode && pendingCount > 0 && !isUploading
                ? { background: 'linear-gradient(135deg, #ec4899 0%, #f97316 100%)' }
                : undefined
            }
          >
            {isUploading ? 'Uploading...' : 'Upload'}
          </button>
          {!quickUploadMode && (
            <button
              onClick={() => uploadAllFiles('answer')}
              disabled={pendingCount === 0 || isUploading}
              className={cn(
                'px-4 py-2 rounded-lg font-semibold transition-colors shadow-sm',
                pendingCount > 0 && !isUploading
                  ? 'text-white hover:brightness-110'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed shadow-none'
              )}
              // Inline gradient so the primary CTA renders the same loud pink-to-
              // orange the rest of the light-UI uses, regardless of which screen
              // hosts this modal (TrainScreen vs TrainScreenLight have different
              // tailwind primary colors). Falls back to a flat grey when disabled.
              style={
                pendingCount > 0 && !isUploading
                  ? { background: 'linear-gradient(135deg, #ec4899 0%, #f97316 100%)' }
                  : undefined
              }
            >
              {isUploading ? 'Uploading...' : 'Upload as Answer'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
